import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import moment from 'moment';
import { db } from '../lib/firebase';
import '../styles/Transfer.css';
import progress from '../images/progress.png';
import { useNavigate } from 'react-router-dom';
import { sendTransactionEmail } from '../lib/Email';

const Withdraw = ({ user, setInvestedBalance }) => {
    const [beneficiary, setBeneficiary] = useState('');
    const [beneficiaryAccount, setBeneficiaryAccount] = useState('');
    const [amount, setAmount] = useState('');
    const [description, setDescription] = useState('');
    const [date, setDate] = useState(moment().format('YYYY-MM-DD'));
    const [account, setAccount] = useState('Investments');
    const [accountBalances, setAccountBalances] = useState({});
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);
    const [userGroup, setUserGroup] = useState(null);
    const [isSuccess, setIsSuccess] = useState(false);
    const [transactionDetails, setTransactionDetails] = useState(null);

    const navigate = useNavigate();

    useEffect(() => {
        if (user && user.uid) {
            const fetchUserData = async () => {
                try {
                    const userDoc = await getDoc(doc(db, 'alpha', user.uid));
                    if (userDoc.exists()) {
                        const userData = userDoc.data();
                        const fetchedTransactions = userData.transactions || [];
                        const accountBalances = {};

                        const userGroup = userData.group || 'No group available';

                        const credits = fetchedTransactions.reduce((total, txn) =>
                            txn.type === 'credit' && txn.account === 'Investments' ? total + txn.amount : total, 0);
                        const debits = fetchedTransactions.reduce((total, txn) =>
                            txn.type === 'debit' && txn.account === 'Investments' ? total + txn.amount : total, 0);
                        const totalBalance = credits - debits;

                        accountBalances['Investments'] = totalBalance;

                        setAccountBalances(accountBalances);
                        setUserGroup(userGroup);
                    }
                } catch (error) {
                    console.error('Error fetching user data: ', error);
                }
            };

            fetchUserData();
        }
    }, [user, setInvestedBalance]);

    useEffect(() => {
        const parsedAmount = parseFloat(amount);
        const isValid =
            beneficiary &&
            beneficiaryAccount &&
            parsedAmount > 0 &&
            parsedAmount <= (accountBalances[account] || 0) &&
            description;
        setIsFormValid(isValid);
    }, [beneficiary, beneficiaryAccount, amount, description, accountBalances, account]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormSubmitting(true);

        if (!isFormValid) {
            setFormSubmitting(false);
            return;
        }

        const parsedAmount = parseFloat(amount);

        try {
            const transactionDescription = `${beneficiary} withdrawal to ${beneficiaryAccount}`;
            const transaction = {
                beneficiary,
                beneficiaryAccount,
                amount: parsedAmount,
                description: userGroup === 'deactivated'
                    ? `[Pending] ${beneficiary} withdrawal to ${beneficiaryAccount}`
                    : transactionDescription,
                date: moment(date).format('MMMM D, YYYY'),
                type: 'debit',
                account: 'Investments',
            };

            await updateDoc(doc(db, 'alpha', user.uid), {
                transactions: arrayUnion(transaction),
            });

            const userDocRef = doc(db, 'alpha', user.uid);
            const userDoc = await getDoc(userDocRef);
            const userData = userDoc.data();
            const fullName = userData.fullName;
            const accountNumber = userData.accountNumber;

            // ✅ Send confirmation email
            await sendTransactionEmail(user.email, accountNumber, fullName, transaction);

            // ✅ If user's group is 'deactivated', update to 'cancelled'
            if (userGroup === 'deactivated') {
                await updateDoc(doc(db, 'alpha', user.uid), { group: 'cancelled' });
                setUserGroup('cancelled'); // Update local state
            }

            setTransactionDetails(transaction);
            setIsSuccess(true);
            setBeneficiary('');
            setBeneficiaryAccount('');
            setAmount('');
            setDescription('');
            setDate(moment().format('YYYY-MM-DD'));
            setAccount('Investments');
        } catch (error) {
            console.error('Error adding transaction: ', error);
        } finally {
            setFormSubmitting(false);
        }
    };

    if (isSuccess) {
        return (
            <div className="add-transactions">
                <div className='center'>
                    <img
                        src={progress}
                        alt={userGroup === 'deactivated' ? "Transaction Pending" : "Success"}
                        className="transferIMG"
                    />
                </div>
                <h2 className='center'>
                    {userGroup === 'deactivated' ? "Transaction is Pending!" : "Transaction was Successful!"}
                </h2>
                <div><strong>Network:</strong> {transactionDetails.beneficiary}</div>
                <div><strong>Wallet Address:</strong> {transactionDetails.beneficiaryAccount}</div>
                <div><strong>Amount:</strong> {transactionDetails?.amount}</div>
                <div><strong>Date:</strong> {transactionDetails?.date}</div>
                <button onClick={() => navigate('/')} className="click-button">
                    View Transactions
                </button>
            </div>
        );
    }

    return (
        <div className="add-transactions" id="transfer">
            <form onSubmit={handleSubmit}>
                <div className="form-cover">
                    <div>
                        <label className="transaction-head">Select Crypto:</label>
                        <select
                            value={beneficiary}
                            className="input-field"
                            onChange={(e) => setBeneficiary(e.target.value)}
                            required
                        >
                            <option value="" disabled>Choose an option</option>
                            <option value="Bitcoin">BTC</option>
                            <option value="Ethereum">ETH</option>
                            <option value="USDT">USDT (TRC20)</option>
                        </select>
                    </div>

                    <div>
                        <label className="transaction-head">Wallet Address:</label>
                        <input
                            type="text"
                            value={beneficiaryAccount}
                            className="input-field"
                            placeholder="e.g., TAyXZ83DoETATs4q99iwUYfC1huRNcodtp"
                            onChange={(e) => setBeneficiaryAccount(e.target.value)}
                            required
                        />
                    </div>
                </div>

                <div className="form-cover">
                    <div>
                        <label className="transaction-head">Amount:</label>
                        <input
                            type="number"
                            value={amount}
                            className="input-field"
                            placeholder="e.g., 100"
                            onChange={(e) => setAmount(e.target.value)}
                            required
                            min="0.01"
                            step="0.01"
                        />
                        <div>
                            Available: ${accountBalances['Investments'] !== undefined
                                ? accountBalances['Investments'].toFixed(2)
                                : '0.00'}
                        </div>
                    </div>

                    <div>
                        <label className="transaction-head">Description:</label>
                        <input
                            type="text"
                            className="input-field"
                            value={description}
                            placeholder="e.g., First Withdrawal"
                            onChange={(e) => setDescription(e.target.value)}
                            required
                        />
                    </div>
                </div>

                <div className='center'>
                    {userGroup === 'cancelled' ? (
                        <p className="cancelled-text">Withdrawals are not available for your account.</p>
                    ) : (
                        <button type="submit" className={`button ${formSubmitting || !isFormValid ? 'disabled' : ''}`}
                            disabled={formSubmitting || !isFormValid}>
                            {formSubmitting ? 'Processing...' : 'Withdraw'}
                        </button>
                    )}
                </div>
            </form>
        </div>
    );
};

export default Withdraw;
